import type { Questionnaire, QuestionnaireItem } from "@medplum/fhirtypes";

import {
  PostpartumClientQuestionnaireType,
  PostpartumNewbornQuestionnaireType,
} from "@/encounters/components/EncounterLabor/constants/postpartum-flowsheets";
import { FAHRENHEIT_TEMPERATURE_UNIT_CODING } from "@/encounters/constants/vital-sign";
import { Extensions } from "@/fhir/enums/extensions";

const PLACEHOLDER_URL = Extensions.QuestionnaireFieldPlaceholder;
const RIGHT_SECTION_URL = Extensions.QuestionnaireFieldRightSection;
const HIDDEN_URL = Extensions.QuestionnaireItemHidden;

const NEWBORN_VITALS_ITEM_ORDER = [
  "newbornVitalsDateTime",
  "newbornVitalsHeartRate",
  "newbornVitalsTemperature",
  "newbornVitalsTemperatureMethod",
  "newbornVitalsTemperatureMethodOther",
  "newbornVitalsRespiratoryRate",
] as const;

/**
 * Resolves the stable questionnaire identity used for IPP / Newborn vitals branching.
 * Prefers `name`, then falls back to `id`, so callers stay aligned if Medplum omits one of them.
 */
export function getPostpartumVitalsQuestionnaireIdentity(
  questionnaire: Questionnaire | null | undefined,
): string | undefined {
  return questionnaire?.name ?? questionnaire?.id;
}

/**
 * Returns true when the questionnaire is labor postpartum Client IPP Vitals or Newborn Vitals.
 */
export function isPostpartumVitalsQuestionnaire(
  questionnaire: Questionnaire | null | undefined,
): boolean {
  const identity = getPostpartumVitalsQuestionnaireIdentity(questionnaire);
  return (
    identity === PostpartumClientQuestionnaireType.ClientIppVitals ||
    identity === PostpartumNewbornQuestionnaireType.NewbornVitals
  );
}

/**
 * Returns true when the questionnaire is Client IPP Vitals.
 */
export function isClientIppVitalsQuestionnaire(
  questionnaire: Questionnaire | null | undefined,
): boolean {
  return (
    getPostpartumVitalsQuestionnaireIdentity(questionnaire) ===
    PostpartumClientQuestionnaireType.ClientIppVitals
  );
}

/**
 * Returns true when the questionnaire is Newborn Vitals.
 */
export function isNewbornVitalsQuestionnaire(
  questionnaire: Questionnaire | null | undefined,
): boolean {
  return (
    getPostpartumVitalsQuestionnaireIdentity(questionnaire) ===
    PostpartumNewbornQuestionnaireType.NewbornVitals
  );
}

/**
 * Ensures new IPP / Newborn vitals entries persist Fahrenheit when the unit dropdown is hidden.
 */
export function seedDefaultPostpartumVitalsTemperatureUnit(
  questionnaire: Questionnaire,
  values: Record<string, unknown>,
): Record<string, unknown> {
  if (!isPostpartumVitalsQuestionnaire(questionnaire)) return values;

  const isIpp = isClientIppVitalsQuestionnaire(questionnaire);
  const groupKey = isIpp ? "ippVitalsTemperature" : "newbornVitalsTemperature";
  const unitKey = isIpp ? "ippVitalsTemperatureUnit" : "newbornVitalsTemperatureUnit";
  const group = (values[groupKey] as Record<string, unknown> | undefined) ?? {};
  if (group[unitKey]) return values;

  return {
    ...values,
    [groupKey]: {
      ...group,
      [unitKey]: FAHRENHEIT_TEMPERATURE_UNIT_CODING,
    },
  };
}

/**
 * Applies vitals chrome to IPP / Newborn questionnaire definitions in memory so drawers match
 * native vitals even when Medplum still has an older seeded Questionnaire.
 */
export function normalizePostpartumVitalsQuestionnaire(
  questionnaire: Questionnaire,
): Questionnaire {
  if (!isPostpartumVitalsQuestionnaire(questionnaire) || !questionnaire.item) {
    return questionnaire;
  }

  const cloned = structuredClone(questionnaire);
  if (!cloned.item) return questionnaire;

  cloned.item = cloned.item.map((item) => normalizeQuestionnaireItem(item));

  const firstItem = cloned.item[0];
  if (isNewbornVitalsQuestionnaire(cloned) && firstItem?.item) {
    firstItem.item = reorderNewbornVitalsItems(firstItem.item);
  }

  return cloned;
}

function normalizeQuestionnaireItem(item: QuestionnaireItem): QuestionnaireItem {
  let nextItem: QuestionnaireItem = { ...item, item: item.item?.map(normalizeQuestionnaireItem) };

  if (
    nextItem.linkId === "ippVitalsBloodPressureSystolic" ||
    nextItem.linkId?.endsWith("BloodPressureSystolic")
  ) {
    nextItem = replacePlaceholderWithRightSection(nextItem, "Systolic");
  }

  if (
    nextItem.linkId === "ippVitalsBloodPressureDiastolic" ||
    nextItem.linkId?.endsWith("BloodPressureDiastolic")
  ) {
    nextItem = replacePlaceholderWithRightSection(nextItem, "Diastolic");
  }

  if (nextItem.linkId === "ippVitalsPulse") {
    nextItem = {
      ...nextItem,
      text: "Heart Rate",
      extension: upsertStringExtension(removeExtension(nextItem.extension, PLACEHOLDER_URL), {
        url: RIGHT_SECTION_URL,
        valueString: "bpm",
      }),
    };
  }

  if (nextItem.linkId === "newbornVitalsTemperatureMethod") {
    nextItem = { ...nextItem, text: nextItem.text || "Method" };
  }

  if (
    nextItem.linkId === "ippVitalsTemperatureUnit" ||
    nextItem.linkId === "newbornVitalsTemperatureUnit"
  ) {
    nextItem = {
      ...nextItem,
      extension: upsertBooleanExtension(nextItem.extension, HIDDEN_URL, true),
    };
  }

  return nextItem;
}

function reorderNewbornVitalsItems(items: QuestionnaireItem[]): QuestionnaireItem[] {
  const prioritizedIds = new Set<string>(NEWBORN_VITALS_ITEM_ORDER);
  const prioritized = NEWBORN_VITALS_ITEM_ORDER.reduce<QuestionnaireItem[]>((ordered, linkId) => {
    const match = items.find((item) => item.linkId === linkId);
    if (match) ordered.push(match);
    return ordered;
  }, []);
  const remaining = items.filter((item) => !item.linkId || !prioritizedIds.has(item.linkId));
  return [...prioritized, ...remaining];
}

function replacePlaceholderWithRightSection(
  item: QuestionnaireItem,
  chip: string,
): QuestionnaireItem {
  return {
    ...item,
    extension: upsertStringExtension(removeExtension(item.extension, PLACEHOLDER_URL), {
      url: RIGHT_SECTION_URL,
      valueString: chip,
    }),
  };
}

function removeExtension(
  extensions: QuestionnaireItem["extension"],
  url: string,
): NonNullable<QuestionnaireItem["extension"]> {
  return extensions?.filter((extension) => extension.url !== url) ?? [];
}

function upsertStringExtension(
  extensions: QuestionnaireItem["extension"],
  extension: { url: string; valueString: string },
): NonNullable<QuestionnaireItem["extension"]> {
  return [...removeExtension(extensions, extension.url), extension];
}

function upsertBooleanExtension(
  extensions: QuestionnaireItem["extension"],
  url: string,
  valueBoolean: boolean,
): NonNullable<QuestionnaireItem["extension"]> {
  return [...removeExtension(extensions, url), { url, valueBoolean }];
}
